<script setup lang="ts">
import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router'
import ComparisonTable from '../components/ComparisonTable.vue'
import { comparisonRows, comparisonRowsEn } from '../data/content'

const isEn = useRoute().meta.locale === 'en'
const rows = isEn ? comparisonRowsEn : comparisonRows
const faq = isEn ? [
  ['What is PentestCamp?', 'PentestCamp is a platform for recurring, hands-on penetration-testing courses. It combines the curriculum, isolated labs, groups, deadlines and progress history.'],
  ['Who is PentestCamp for?', 'Universities, lecturers and training teams that want to run their own lab programme and retain control of the curriculum.'],
  ['Does PentestCamp run in the cloud and on-premise?', 'Yes. PentestCamp can run as a cloud service or within an organisation’s own infrastructure.'],
] : [
  ['Czym jest PentestCamp?', 'PentestCamp to platforma do prowadzenia regularnych, praktycznych zajęć z testów penetracyjnych. Łączy program, izolowane laboratoria, grupy, terminy i historię postępu.'],
  ['Dla kogo jest przeznaczony PentestCamp?', 'Dla uczelni, prowadzących oraz zespołów szkoleniowych, które chcą prowadzić własny cykl laboratoriów i zachować kontrolę nad jego programem.'],
  ['Czy PentestCamp działa w chmurze i on-premise?', 'Tak. PentestCamp może działać jako usługa cloud albo we własnej infrastrukturze organizacji.'],
]

useHead({
  script: [{
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map(([name, text]) => ({ '@type': 'Question', name, acceptedAnswer: { '@type': 'Answer', text } })),
    }),
  }],
})
</script>

<template>
  <section class="comparison section-pad page-first" aria-labelledby="comparison-title">
    <div class="section-heading split-heading">
      <div><p class="section-index">01 / {{ isEn ? 'LANDSCAPE' : 'KRAJOBRAZ' }}</p><h1 id="comparison-title">{{ isEn ? 'Not another lab catalogue. A tool for running a complete course.' : 'Nie kolejny katalog zadań. Narzędzie do prowadzenia cyklu zajęć.' }}</h1></div>
      <p>{{ isEn ? 'These platforms solve different problems. This comparison shows their natural focus rather than pretending to be a feature ranking.' : 'Platformy rozwiązują różne problemy. Zestawienie pokazuje ich naturalny punkt ciężkości, nie udaje rankingu funkcji.' }}</p>
    </div>
    <ComparisonTable :rows="rows" :lang="isEn ? 'en' : 'pl'" />
    <p class="sources-note">{{ isEn ? 'Based on public vendor materials, verified 14 October 2026:' : 'Opis na podstawie publicznych materiałów producentów, zweryfikowanych 14.10.2026:' }} <a href="https://ctfd.io/" target="_blank" rel="noreferrer">CTFd</a>, <a href="https://www.hackthebox.com/business" target="_blank" rel="noreferrer">Hack The Box</a>, <a href="https://tryhackme.com/business" target="_blank" rel="noreferrer">TryHackMe</a> {{ isEn ? 'and' : 'i' }} <a href="https://cdex.cloud/" target="_blank" rel="noreferrer">CDeX</a>.</p>
  </section>

  <section class="faq section-pad" aria-labelledby="faq-title">
    <div class="faq-heading"><p class="section-index">02 / {{ isEn ? 'IN BRIEF' : 'W SKRÓCIE' }}</p><h2 id="faq-title">{{ isEn ? 'Key answers about PentestCamp.' : 'Najważniejsze odpowiedzi o PentestCamp.' }}</h2></div>
    <dl class="faq-list">
      <div v-for="([question, answer], index) in faq" :key="question"><dt>{{ index === 1 && !isEn ? 'Dla kogo jest przeznaczony?' : index === 2 && !isEn ? 'Czy działa w chmurze i on-premise?' : question }}</dt><dd>{{ answer }}</dd></div>
    </dl>
  </section>
</template>
